/**
 * StatusDot Component (TASK-337, AC-5 / FR-003)
 *
 * Shared "colored dot + text" status chip from the redesigned list pattern,
 * extracted from the duplicated copies in DatasetsPage, DatasetDetailPage and
 * TelephonyPage. The semantic `color` prop maps to the donor Tailwind classes.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type StatusDotColor = 'gray' | 'green' | 'yellow' | 'blue' | 'red';

const DOT_CLASSES: Record<StatusDotColor, string> = {
  gray: 'bg-gray-400',
  green: 'bg-green-500',
  yellow: 'bg-yellow-400',
  blue: 'bg-blue-500',
  red: 'bg-red-500',
};

const TEXT_CLASSES: Record<StatusDotColor, string> = {
  gray: 'text-gray-600',
  green: 'text-green-700',
  yellow: 'text-yellow-700',
  blue: 'text-blue-700',
  red: 'text-red-700',
};

export interface StatusDotProps extends HTMLAttributes<HTMLSpanElement> {
  color: StatusDotColor;
  label: ReactNode;
}

export function StatusDot({
  color,
  label,
  className,
  ...props
}: StatusDotProps): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium',
        TEXT_CLASSES[color],
        className
      )}
      {...props}
    >
      <span
        data-testid="status-dot"
        className={cn(
          'w-1.5 h-1.5 rounded-full flex-shrink-0',
          DOT_CLASSES[color]
        )}
      />
      {label}
    </span>
  );
}
